import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  getActiveProgramme,
  getProgrammes as apiGetProgrammes,
  activateProgramme as apiActivateProgramme,
  updateProgramme as apiUpdateProgramme,
  createProgramme as apiCreateProgramme,
  assignProgrammeSession as apiAssignSession,
  missProgrammeSession as apiMissSession,
  clearProgrammeSession as apiClearSession,
  reorderProgrammeWeek as apiReorderWeek,
  resetProgrammeWeek as apiResetWeek,
  setProgrammeDeloads as apiSetDeloads,
  saveProgrammeCheckin as apiSaveCheckin,
  deleteProgrammeCheckin as apiDeleteCheckin,
  shiftProgramme as apiShift,
  deleteProgramme as apiDeleteProgramme,
  placeProgrammeSession as apiPlaceSession,
  skipProgrammeSession as apiSkipSession,
  linkProgrammeSession as apiLinkSession,
} from "../services/api";
import { planWeek } from "../config/programmePlanner";
import { currentDeloads } from "../utils/programmeWeeks";
import { currentProgrammeWeek } from "../utils/programmeSchedule";

export function useProgramme() {
  const [programme, setProgramme] = useState(null);
  const [programmes, setProgrammes] = useState([]); // #58 library list
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  // The latest programme for callbacks that run later, like a toast's Undo.
  const programmeRef = useRef(null);
  programmeRef.current = programme;

  const refreshProgrammes = useCallback(async () => {
    if (!isAuthenticated) {
      setProgrammes([]);
      return [];
    }
    try {
      const list = await apiGetProgrammes();
      setProgrammes(Array.isArray(list) ? list : []);
      return list;
    } catch {
      return [];
    }
  }, [isAuthenticated]);

  // Fetch active programme + library on mount / auth change
  useEffect(() => {
    if (!isAuthenticated) {
      setProgramme(null);
      setProgrammes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      getActiveProgramme().catch(() => null),
      apiGetProgrammes().catch(() => []),
    ])
      .then(([active, list]) => {
        setProgramme(active);
        setProgrammes(Array.isArray(list) ? list : []);
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const hasActiveProgramme = !!programme;

  // Derived: the current calendar week (Monday to Sunday) of the programme (#92)
  const currentWeek = useMemo(
    () => (programme ? currentProgrammeWeek(programme) : 0),
    [programme],
  );

  // Derived: stats
  const stats = useMemo(() => {
    if (!programme) return { total: 0, completed: 0, missed: 0, assigned: 0 };
    // Skipped sessions (#196, and an imported programme's weeks before the
    // one you started at) aren't part of what's left to do.
    const all = programme.weeks.flatMap((w) => w.sessions).filter((s) => s.status !== "skipped");
    return {
      total: all.length,
      completed: all.filter((s) => s.status === "completed").length,
      missed: all.filter((s) => s.status === "missed").length,
      assigned: all.filter((s) => s.plan_data !== null).length,
    };
  }, [programme]);

  const createProgramme = useCallback(
    async (config) => {
      const result = await apiCreateProgramme(config);
      setProgramme(result);
      refreshProgrammes();
      return result;
    },
    [refreshProgrammes],
  );

  const assignSession = useCallback(
    // `extra` (#184): { adapted } or { built_by } for a mate's session.
    async (programmeSessionId, { label, plan_data, ...extra }) => {
      if (!programme) return;

      // Optimistic update
      const prev = programme;
      setProgramme((p) => {
        if (!p) return p;
        const updated = JSON.parse(JSON.stringify(p));
        for (const week of updated.weeks) {
          for (const s of week.sessions) {
            if (s.id === programmeSessionId) {
              s.label = label;
              s.plan_data = plan_data;
            }
          }
        }
        return updated;
      });

      try {
        await apiAssignSession(programme.id, programmeSessionId, {
          label,
          plan_data,
          ...extra,
        });
      } catch (err) {
        console.error("[Programme] Failed to assign session:", err);
        setProgramme(prev); // Rollback
        throw err;
      }
    },
    [programme],
  );

  const markMissed = useCallback(
    async (programmeSessionId) => {
      if (!programme) return;

      const prev = programme;
      setProgramme((p) => {
        if (!p) return p;
        const updated = JSON.parse(JSON.stringify(p));
        for (const week of updated.weeks) {
          for (const s of week.sessions) {
            if (s.id === programmeSessionId) {
              s.status = "missed";
            }
          }
        }
        return updated;
      });

      try {
        await apiMissSession(programme.id, programmeSessionId);
      } catch (err) {
        console.error("[Programme] Failed to mark missed:", err);
        setProgramme(prev);
        throw err;
      }
    },
    [programme],
  );

  // #59: clear an assigned session back to its empty skeleton slot.
  const clearSession = useCallback(
    async (programmeSessionId) => {
      if (!programme) return;
      const prev = programme;
      setProgramme((p) => {
        if (!p) return p;
        const updated = JSON.parse(JSON.stringify(p));
        for (const week of updated.weeks) {
          for (const s of week.sessions) {
            if (s.id === programmeSessionId) {
              s.label = null;
              s.plan_data = null;
              s.status = "pending";
              s.workout_session_id = null;
              s.completed_at = null;
              s.duration_minutes = null;
              s.session_notes = null;
              s.session_rating = null;
            }
          }
        }
        return updated;
      });
      try {
        await apiClearSession(programme.id, programmeSessionId);
      } catch (err) {
        console.error("[Programme] Failed to clear session:", err);
        setProgramme(prev);
        throw err;
      }
    },
    [programme],
  );

  // #196 week pool: put a session on a weekday of its week (0 Mon - 6 Sun),
  // into the pool (null), or skip it. The server returns the programme.
  const placeSession = useCallback(async (sessionId, weekday) => {
    const programme = programmeRef.current;
    if (!programme) return null;
    const result = await apiPlaceSession(programme.id, sessionId, weekday);
    if (result) setProgramme(result);
    return result;
  }, []);

  const skipSession = useCallback(async (sessionId) => {
    const programme = programmeRef.current;
    if (!programme) return null;
    const result = await apiSkipSession(programme.id, sessionId);
    if (result) setProgramme(result);
    return result;
  }, []);

  // #196: count a session already done as this programme session.
  const linkSession = useCallback(async (sessionId, workoutSessionId) => {
    const programme = programmeRef.current;
    if (!programme) return null;
    const result = await apiLinkSession(programme.id, sessionId, workoutSessionId);
    if (result) setProgramme(result);
    return result;
  }, []);

  // #59: reorder one week's sessions to a new order of session ids. Optimistic
  // reorder (reindexes day_index locally), reconciled with the server's
  // authoritative programme on success.
  const reorderWeek = useCallback(
    async (weekNumber, orderedIds) => {
      if (!programme) return;
      const prev = programme;
      setProgramme((p) => {
        if (!p) return p;
        const updated = JSON.parse(JSON.stringify(p));
        const week = updated.weeks.find((w) => w.week_number === weekNumber);
        if (!week) return p;
        const byId = new Map(week.sessions.map((s) => [s.id, s]));
        const reordered = orderedIds
          .map((id) => byId.get(id))
          .filter(Boolean);
        if (reordered.length !== week.sessions.length) return p;
        // Keep the positions the week holds: a short first week (#125)
        // starts after day 0.
        const positions = week.sessions.map((s) => s.day_index).sort((a, b) => a - b);
        reordered.forEach((s, i) => {
          s.day_index = positions[i];
        });
        week.sessions = reordered;
        return updated;
      });
      try {
        const result = await apiReorderWeek(programme.id, weekNumber, orderedIds);
        if (result) setProgramme(result);
      } catch (err) {
        console.error("[Programme] Failed to reorder week:", err);
        setProgramme(prev);
        throw err;
      }
    },
    [programme],
  );

  // #59: reset one week back to its plan. The app re-plans the week (#92) and
  // the server returns the refreshed programme with that week's slots cleared.
  const resetWeek = useCallback(
    async (weekNumber, userProfile) => {
      if (!programme) return;
      const slots = planWeek(programme, weekNumber, userProfile);
      const result = await apiResetWeek(programme.id, weekNumber, slots);
      if (result) setProgramme(result);
      return result;
    },
    [programme],
  );

  // #94: set the deload weeks, re-planning every week whose status changes.
  const setDeloads = useCallback(
    async (deloadWeeks, userProfile) => {
      const programme = programmeRef.current;
      if (!programme) return null;
      const before = new Set(currentDeloads(programme));
      const after = new Set(deloadWeeks.map(Number));
      const changed = [...new Set([...before, ...after])].filter(
        (w) => before.has(w) !== after.has(w),
      );
      const next = { ...programme, deload_weeks: [...after] };
      const slots = changed.flatMap((w) => planWeek(next, w, userProfile));
      await apiSetDeloads(programme.id, [...after].sort((a, b) => a - b), slots);
      const active = await getActiveProgramme().catch(() => null);
      if (active) setProgramme(active);
      return active;
    },
    [],
  );

  // #92: repeat last week. Its missed days reopen for this week and the rest
  // of the programme moves a week later. Returns the reopened ids for Undo.
  const repeatLastWeek = useCallback(async () => {
    const programme = programmeRef.current;
    if (!programme) return null;
    const last = currentProgrammeWeek(programme) - 1;
    const week = (programme.weeks || []).find((w) => w.week_number === last);
    const reopen = (week?.sessions || []).filter((s) => s.status === "missed").map((s) => s.id);
    if (last < 1) return null;
    await apiShift(programme.id, { weeks: 1, reopen });
    const active = await getActiveProgramme().catch(() => null);
    if (active) setProgramme(active);
    return { week: last, reopen };
  }, []);

  const undoRepeatLastWeek = useCallback(async ({ reopen = [] } = {}) => {
    const programme = programmeRef.current;
    if (!programme) return;
    await apiShift(programme.id, { weeks: -1, miss: reopen });
    const active = await getActiveProgramme().catch(() => null);
    if (active) setProgramme(active);
  }, []);

  // #94: record, or undo, the user's answer about a finished week.
  const saveCheckin = useCallback(
    async (weekNumber, { answer, action }) => {
      const programme = programmeRef.current;
      if (!programme) return null;
      const checkin = await apiSaveCheckin(programme.id, weekNumber, { answer, action });
      setProgramme((p) =>
        p && p.id === programme.id
          ? {
              ...p,
              checkins: [
                ...(p.checkins || []).filter((c) => c.week_number !== checkin.week_number),
                checkin,
              ],
            }
          : p,
      );
      return checkin;
    },
    [],
  );

  const undoCheckin = useCallback(
    async (weekNumber) => {
      const programme = programmeRef.current;
      if (!programme) return;
      await apiDeleteCheckin(programme.id, weekNumber);
      setProgramme((p) =>
        p && p.id === programme.id
          ? { ...p, checkins: (p.checkins || []).filter((c) => c.week_number !== weekNumber) }
          : p,
      );
    },
    [],
  );

  // #58: delete any programme (defaults to the active one). If the active one
  // is removed, no programme becomes active until the user picks another.
  const removeProgramme = useCallback(
    async (id) => {
      const targetId = id ?? programme?.id;
      if (!targetId) return;
      await apiDeleteProgramme(targetId);
      if (programme?.id === targetId) setProgramme(null);
      refreshProgrammes();
    },
    [programme, refreshProgrammes],
  );

  // #58: make a library programme the active one (others stand down).
  const switchProgramme = useCallback(
    async (id, firstDay) => {
      const result = await apiActivateProgramme(id, firstDay);
      if (result) setProgramme(result);
      refreshProgrammes();
      return result;
    },
    [refreshProgrammes],
  );

  // #58: edit a programme (metadata or structural). Structural changes
  // regenerate the skeleton server-side. Re-fetch the active programme so the
  // timeline reflects the change.
  const editProgramme = useCallback(
    async (id, data) => {
      const result = await apiUpdateProgramme(id, data);
      if (programme?.id === id) {
        const active = await getActiveProgramme().catch(() => null);
        if (active) setProgramme(active);
      }
      refreshProgrammes();
      return result;
    },
    [programme, refreshProgrammes],
  );

  const refreshProgramme = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getActiveProgramme();
      setProgramme(data);
    } catch {
      // Silently fail on refresh
    }
  }, [isAuthenticated]);

  return {
    programme,
    programmes,
    loading,
    hasActiveProgramme,
    currentWeek,
    stats,
    createProgramme,
    assignSession,
    markMissed,
    clearSession,
    reorderWeek,
    placeSession,
    skipSession,
    linkSession,
    resetWeek,
    setDeloads,
    saveCheckin,
    undoCheckin,
    repeatLastWeek,
    undoRepeatLastWeek,
    switchProgramme,
    editProgramme,
    removeProgramme,
    refreshProgramme,
    refreshProgrammes,
  };
}

export default useProgramme;
